import React, {ReactNode, useState} from 'react';
import {Breadcrumbs, Divider, Grid, LinearProgress, Paper} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import makeStyles from '@mui/styles/makeStyles';
import {FILE_SEPARATOR, UNIX_FILE_SEPARATOR, useBrowseList} from 'redux/server/hooks/useBrowseList';
import FolderItem from 'features/layout/DialogRoutes/dialogs/FileManagerDialog/FolderItem';
import {isEmpty, sortBy} from 'lodash';
import Dropdown from 'components/inputs/Dropdown';
import {IFileManagerDirectoryContent} from 'types/diplomat/file_manager/FileManagerDirectoryContent';
import FileItem from 'features/layout/DialogRoutes/dialogs/FileManagerDialog/FileItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Button from 'components/Button';
import {useTranslation} from 'react-i18next';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import {getArrayOfBreadcrumbs} from 'common/stringUtils';
import Checkbox from 'components/inputs/Checkbox';

const useStyles = makeStyles((theme) => ({
   breadcrumbs: {
      padding: '1rem',
   },
   filterDropdown: {
      minWidth: '10rem',
      marginRight: '1rem',
   },
   content: {
      padding: '1rem',
      minHeight: '20rem',
   },
   itemsWrapper: {
      alignItems: 'center',
   },
   fileFolderWrapper: {
      flexGrow: 0,
   },
   buttons: {
      padding: theme.spacing(2, 0),
   },
}));

type FileManagerProps = {
   initialPath?: string;
   directoriesOnly?: boolean;
   filesOfType?: string[];
   onSelect: (file: string) => void;
   onCancel: () => void;
   formFooter?: ReactNode;
};

const FileManager = ({
   initialPath = FILE_SEPARATOR,
   directoriesOnly,
   filesOfType = [],
   formFooter,
   onSelect,
   onCancel,
}: FileManagerProps) => {
   const classes = useStyles();
   const {t} = useTranslation();
   const {browseList, loading, fileSeparator, fetchBrowseList} = useBrowseList(directoriesOnly);
   const [filter, setFilter] = React.useState<string>(filesOfType[0]);
   const breadcrumbs = React.useMemo(() => getArrayOfBreadcrumbs(browseList?.cwd?.filePath), [
      browseList,
      fileSeparator,
   ]);

   const [fileName, setFileName] = React.useState<string | undefined>();
   React.useEffect(() => {
      const lastSepIndex = initialPath
         ? initialPath.lastIndexOf(fileSeparator) + 1 ||
           initialPath.lastIndexOf(UNIX_FILE_SEPARATOR) + 1
         : 0;

      const fileName = initialPath?.substring(lastSepIndex, initialPath.length);
      setFileName(fileName);

      if (directoriesOnly) {
         const _path = initialPath?.substring(0, lastSepIndex);
         setPath(_path || FILE_SEPARATOR);
      } else {
         setPath(initialPath || FILE_SEPARATOR);
      }
   }, [directoriesOnly, initialPath]);

   const [path, setPath] = React.useState<string | undefined>();
   React.useEffect(() => {
      if (path) {
         fetchBrowseList(path, filter);
      }
   }, [fetchBrowseList, path, filter]);

   const [selected, _setSelected] = React.useState<string | null>();
   const setSelected = React.useCallback(
      (file: IFileManagerDirectoryContent | null) => {
         if (file) {
            if ((directoriesOnly && file?.isFile) || (!isEmpty(filesOfType) && !file?.isFile))
               return;
         }
         _setSelected(file?.filePath);
      },
      [_setSelected]
   );
   React.useEffect(() => {
      if (fileName) {
         const file = browseList?.files.find((f) => f.name === fileName);
         if (file) {
            setSelected(file);
         }
      }
   }, [browseList?.files, fileName, setSelected]);

   const files = React.useMemo(() => sortBy(browseList?.files, ['isFile', 'name']), [
      browseList?.files,
   ]);

   return (
      <>
         <Paper variant="outlined">
            <Grid
               container
               spacing={2}
               alignItems="center"
               justifyContent="space-between"
               wrap={'nowrap'}
            >
               <Grid item>
                  <Breadcrumbs
                     classes={{root: classes.breadcrumbs}}
                     separator={fileSeparator}
                     aria-label="breadcrumb"
                  >
                     <IconButton size={'small'} onClick={() => fetchBrowseList()}>
                        <HomeOutlinedIcon />
                     </IconButton>
                     {breadcrumbs.map((bc, i) => {
                        const isLast = i === breadcrumbs.length - 1;
                        const handleClick = () => {
                           setSelected(null);
                           setPath(bc.bcPath);
                        };
                        return isLast ? (
                           <Typography color={'textPrimary'} key={bc.bc}>
                              <strong>{bc.bc}</strong>
                           </Typography>
                        ) : (
                           <Link
                              key={bc.bc}
                              component={'button'}
                              variant={'body1'}
                              color={'textPrimary'}
                              onClick={handleClick}
                           >
                              {bc.bc}
                           </Link>
                        );
                     })}
                  </Breadcrumbs>
               </Grid>
               {filter ? (
                  <Grid item className={classes.filterDropdown}>
                     <Dropdown
                        name="filters"
                        options={filesOfType}
                        label={t('Type')}
                        value={filter}
                        onChange={(ev) => setFilter(ev.target.value as string)}
                     />
                  </Grid>
               ) : null}
            </Grid>
            <Divider />
            {loading ? <LinearProgress /> : null}
            <div className={classes.content}>
               <Grid container spacing={1} className={classes.itemsWrapper}>
                  {files?.map((f) => {
                     const isSelected = f.filePath === selected;
                     const onClick = () => setSelected(f);
                     return (
                        <Grid key={f.filePath} item xs={4} className={classes.fileFolderWrapper}>
                           {f.isFile ? (
                              <FileItem name={f.name} selected={isSelected} onClick={onClick} />
                           ) : (
                              <FolderItem
                                 name={f.name}
                                 selected={isSelected}
                                 onClick={onClick}
                                 onDoubleClick={() => {
                                    setSelected(null);
                                    setPath(f.filePath);
                                    // fetchBrowseList(f.filePath, filter);
                                 }}
                              />
                           )}
                        </Grid>
                     );
                  })}
               </Grid>
            </div>
         </Paper>
         {formFooter}
         <Grid container spacing={2} justifyContent={'center'} className={classes.buttons}>
            <Grid item>
               <PrimaryButton
                  type="button"
                  disabled={Boolean(!path || (!selected && !isEmpty(filesOfType)))}
                  onClick={() => onSelect(selected || path!)}
               >
                  {t('Select')}
               </PrimaryButton>
            </Grid>
            <Grid item>
               <Button type="button" onClick={onCancel}>
                  {t('Cancel')}
               </Button>
            </Grid>
         </Grid>
      </>
   );
};

export default React.memo(FileManager);
